import { useCallback, useEffect, useState } from "react";
import type { GraphData } from "../types";

interface GraphDataState {
  data: GraphData | null;
  error: string | null;
  loading: boolean;
}

function isGraphData(value: unknown): value is GraphData {
  if (!value || typeof value !== "object") return false;

  const candidate = value as Partial<GraphData>;
  return Boolean(
    candidate.meta &&
      Array.isArray(candidate.nodes) &&
      Array.isArray(candidate.edges) &&
      candidate.ontology &&
      Array.isArray(candidate.ontology.nodeTypes) &&
      Array.isArray(candidate.ontology.edgeTypes),
  );
}

export function useGraphData() {
  const [requestVersion, setRequestVersion] = useState(0);
  const [state, setState] = useState<GraphDataState>({
    data: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    const controller = new AbortController();

    async function loadGraph() {
      setState((current) => ({ ...current, error: null, loading: true }));

      try {
        const response = await fetch("/data/graph.json", {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`그래프 데이터를 불러오지 못했습니다. (${response.status})`);
        }

        const payload: unknown = await response.json();
        if (!isGraphData(payload)) {
          throw new Error("그래프 데이터 형식이 올바르지 않습니다.");
        }

        setState({ data: payload, error: null, loading: false });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState({
          data: null,
          error: error instanceof Error ? error.message : "알 수 없는 오류가 발생했습니다.",
          loading: false,
        });
      }
    }

    void loadGraph();
    return () => controller.abort();
  }, [requestVersion]);

  const reload = useCallback(() => {
    setRequestVersion((version) => version + 1);
  }, []);

  return { ...state, reload };
}
